// ElevenLabs Conversational AI adapter.
//
// The adapter does NOT import the ElevenLabs SDK at module-eval time.
// Instead it dynamic-imports inside startConversation. Two reasons:
//   1. The widget is enabled by env flag - most pageloads never need the SDK.
//   2. Vendor swap is a one-file change; no top-level vendor import to remove.
//
// The SDK package is loaded lazily so the bundle doesn't pull it on cold start.

import {
  fetchSignedUrl,
  modeToOverrides,
  parseIntent,
  type ConversationHandle,
  type StartConversationInput,
  type VoiceProvider,
} from './index'

interface ElevenLabsSdk {
  Conversation: {
    startSession(opts: {
      signedUrl: string
      overrides: { agent: { textOnly: boolean } }
      clientTools: Record<string, (args: unknown) => unknown>
    }): Promise<{ id: string; endSession: () => Promise<void> }>
  }
}

/* v8 ignore start */
const SDK_PACKAGE = '@elevenlabs/client'
async function loadSdk(): Promise<ElevenLabsSdk> {
  // The SDK ships as @elevenlabs/client. Dynamic-import keeps it out of the
  // initial bundle. The variable indirection prevents Vite from statically
  // resolving the import at build time (the package is installed only when
  // the concierge feature is wired).
  const mod: unknown = await import(/* @vite-ignore */ SDK_PACKAGE)
  return mod as ElevenLabsSdk
}
/* v8 ignore stop */

/* v8 ignore start */
function buildClientTools(input: StartConversationInput): Record<string, (args: unknown) => unknown> {
  return {
    open_booking: (args) => {
      const intent = parseIntent({ tool: 'open_booking', arguments: args })
      if (intent) input.onIntent?.(intent)
    },
    get_live_pricing: (args) => {
      const intent = parseIntent({ tool: 'get_live_pricing', arguments: args })
      if (intent) input.onIntent?.(intent)
    },
    contact_human: () => {
      const intent = parseIntent({ tool: 'contact_human' })
      if (intent) input.onIntent?.(intent)
    },
  }
}
/* v8 ignore stop */

export const elevenLabsProvider: VoiceProvider = {
  async startConversation(input: StartConversationInput): Promise<ConversationHandle> {
    const overrides = modeToOverrides(input.mode)
    const signedUrl = await fetchSignedUrl(input.signedUrlEndpoint, input.agentId)

    /* v8 ignore start */
    const sdk = await loadSdk()
    const session = await sdk.Conversation.startSession({
      signedUrl,
      overrides: { agent: { textOnly: overrides.textOnly } },
      clientTools: buildClientTools(input),
    })
    return {
      sessionId: session.id,
      end: () => session.endSession(),
    }
    /* v8 ignore stop */
  },
}
